import React, { createContext, useCallback, useEffect, useState } from 'react';
import axiosInstance, { ensureCsrfCookie, refreshAppSession } from 'src/common/axiosInstance';
import {
  beginDeveloperLogin,
  clearDeveloperTokens,
  isDeveloperOAuthEnabled,
} from 'src/services/developerOAuth';

const AuthContext = createContext();
const applicationKey = process.env.REACT_APP_CURRENT_APPLICATION || 'app';
const AUTH_PROFILE_CACHE_KEY = `autoappy.${applicationKey}.auth_profile`;
const DEFAULT_PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;

function readCachedProfile() {
  try {
    const cached = JSON.parse(localStorage.getItem(AUTH_PROFILE_CACHE_KEY));
    if (!cached?.data || !cached?.expiresAt || cached.expiresAt <= Date.now()) {
      localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
      return null;
    }
    return cached.data;
  } catch {
    localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
    return null;
  }
}

function cacheProfile(profile) {
  const serverTtl = Number(profile?.profile_cache_ttl_seconds) * 1000;
  const ttl = Number.isFinite(serverTtl) && serverTtl > 0
    ? serverTtl
    : DEFAULT_PROFILE_CACHE_TTL_MS;

  try {
    localStorage.setItem(AUTH_PROFILE_CACHE_KEY, JSON.stringify({
      data: profile,
      cachedAt: Date.now(),
      expiresAt: Date.now() + ttl,
    }));
  } catch {
    // Authentication remains usable when browser storage is unavailable.
  }
}

const AuthProvider = ({ children }) => {
  const [cachedProfile] = useState(readCachedProfile);
  const [auth, setAuth] = useState(Boolean(cachedProfile));
  const [currentUser, setCurrentUser] = useState(cachedProfile);
  const [loading, setLoading] = useState(!cachedProfile);
  const [authError, setAuthError] = useState(null);

  const refreshSession = useCallback(async () => {
    try {
      const response = await refreshAppSession();
      const profile = response.data.data;
      cacheProfile(profile);
      setCurrentUser(profile);
      setAuth(true);
      setAuthError(null);
      return profile;
    } catch (error) {
      localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
      setCurrentUser(null);
      setAuth(false);
      setAuthError(error.response?.status === 403 ? 'access_denied' : null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshSession(); }, [refreshSession]);

  useEffect(() => {
    const revalidateWhenActive = () => {
      if (document.visibilityState === 'visible') refreshSession();
    };

    window.addEventListener('focus', revalidateWhenActive);
    document.addEventListener('visibilitychange', revalidateWhenActive);

    return () => {
      window.removeEventListener('focus', revalidateWhenActive);
      document.removeEventListener('visibilitychange', revalidateWhenActive);
    };
  }, [refreshSession]);

  const beginLogin = useCallback((returnTo = `${window.location.pathname}${window.location.search}`) => {
    if (isDeveloperOAuthEnabled()) return beginDeveloperLogin(returnTo);
    const params = new URLSearchParams({ return_to: returnTo || '/' });
    const workspace = new URLSearchParams(window.location.search).get('workspace');
    if (workspace) params.set('workspace', workspace);
    window.location.assign(`${process.env.REACT_APP_BASE_URL}/auth/autoappy/redirect?${params.toString()}`);
    return Promise.resolve();
  }, []);

  const clearLocalState = useCallback(() => {
    localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
    localStorage.removeItem('autoappy_current_workspace_id');
    clearDeveloperTokens();
    setCurrentUser(null);
    setAuth(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await ensureCsrfCookie();
      await axiosInstance.post('/api/auth/logout');
    } finally {
      clearLocalState();
    }
  }, [clearLocalState]);

  return (
    <AuthContext.Provider value={{
      auth, setAuth, currentUser, setCurrentUser, loading, authError,
      isEmailVerified: Boolean(currentUser?.email_verified_at),
      isAdmin: Boolean(currentUser?.is_admin),
      permissions: currentUser?.permissions || [],
      refreshSession, beginLogin, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
